import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import { toastDeliveryOutcome } from "../../utils/describeDeliveryOutcome";

export default function IssueCreditNoteModal({
  isOpen,
  onClose,
  invoice,
  onSuccess,
}) {
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const user = getDecryptedUserFromCookie("gta_mainstateuser");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [sendMail, setSendMail] = useState(true);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAmount("");
      setReason("");
      setSendMail(true);
      setSendWhatsapp(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !invoice) return null;

  const maxCredit = parseFloat(invoice.pending) || 0;
  const today = new Date().toLocaleDateString("en-CA");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const creditAmount = parseFloat(amount);
    if (!Number.isFinite(creditAmount) || creditAmount <= 0) {
      toast.error("Enter a valid credit amount.");
      return;
    }
    if (creditAmount > maxCredit + 0.001) {
      toast.error(`Credit cannot exceed remaining balance (₹${maxCredit.toFixed(2)}).`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${url}/newinvoice/credit-note/${invoice.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: creditAmount,
          reason,
          publish: true,
          send_mail: sendMail,
          send_whatsapp: sendWhatsapp,
          invoice_date: today,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not issue credit note");

      const base = `Credit note ${data.creditNote?.invoice_number} issued`;
      const { message, type } = toastDeliveryOutcome(base, { delivery: data.delivery });
      if (type === "error") toast.error(message);
      else if (type === "warning") toast.warning(message);
      else toast.success(message);
      setAmount("");
      setReason("");
      onSuccess?.(data);
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to issue credit note");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="custom-modal-overlay" onClick={onClose}>
      <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="oms-modal-title">Issue credit note</h2>
        <p style={{ fontSize: 14, color: "#64748b", marginBottom: 16 }}>
          Against invoice <strong>{invoice.invoice_number}</strong>. This reduces the
          customer&apos;s balance without editing the original invoice.
        </p>
        <form className="oms-modal-form" onSubmit={handleSubmit}>
          <div className="oms-form-group">
            <label className="oms-form-label">
              Credit amount (max ₹{maxCredit.toFixed(2)})
            </label>
            <input
              type="number"
              min="0.01"
              max={maxCredit}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount to credit"
              required
            />
          </div>
          <div className="oms-form-group">
            <label className="oms-form-label">Reason / description</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Returned item, billing correction, discount agreed"
              rows={3}
            />
          </div>
          <div className="oms-form-group">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={sendMail}
                onChange={(e) => setSendMail(e.target.checked)}
              />
              Email credit note PDF to customer
            </label>
          </div>
          <div className="oms-form-group">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={sendWhatsapp}
                onChange={(e) => setSendWhatsapp(e.target.checked)}
              />
              Send WhatsApp to customer
            </label>
          </div>
          <div className="oms-form-actions">
            <button type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" disabled={submitting || maxCredit <= 0}>
              {submitting ? "Issuing…" : "Issue credit note"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
